"use client";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { IconTarget, IconSettings, IconCheck, IconChartBar } from "@/lib/tabler-icons";
import { Accion } from "../../data/acciones";
import { Icon } from "@/components/ui/Icons";
import Image from "next/image";
import { SectionHeading, Caption } from "@/components/ui/editorial";

interface ActionSectionProps {
	accion: Accion;
}

export default function ActionSection({ accion }: ActionSectionProps) {
	return (
		<section className="px-4 xl:px-0 py-16 border-t border-border">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				{/* Encabezado */}
				<motion.header
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8 }}
					viewport={{ once: true }}
					className="mb-12"
				>
					<SectionHeading
						index={String(accion.id).padStart(2, "0")}
						title={accion.subtitulo}
						kicker={accion.titulo}
					/>
				</motion.header>

				{/* Banner imagen + descripción */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8 }}
					viewport={{ once: true }}
					className="grid lg:grid-cols-2 gap-12 items-start mb-16"
				>
					{/* Descripción */}
					<div className="space-y-4">
						<ReactMarkdown
								components={{
									p: ({ children }) => (
										<p className="font-body text-lg text-ink/80 leading-relaxed">{children}</p>
									),
									strong: ({ children }) => (
										<strong className="font-semibold text-ink">{children}</strong>
									),
								}}
							>
								{accion.descripcion}
							</ReactMarkdown>
					</div>

					{/* Imagen representativa */}
					<figure>
						<div className="relative w-full aspect-video rounded-xl border border-border overflow-hidden">
							<Image
								src={accion.img}
								alt={accion.titulo}
								fill
								sizes="(max-width: 1024px) 100vw, 50vw"
								className="object-cover"
							/>
						</div>
						<Caption>{accion.subtitulo}</Caption>
					</figure>
				</motion.div>

				{/* Objetivos y Metodología */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, delay: 0.2 }}
					viewport={{ once: true }}
					className="mb-16"
				>
					<div className="mb-10">
						<h3 className="font-display text-2xl font-semibold text-ink">
							Objetivos y Metodología
						</h3>
						<hr className="border-border mt-4" />
					</div>

					<div className="grid lg:grid-cols-2 gap-12">
						{/* Objetivos */}
						<div className="space-y-4">
							<h4 className="font-display text-lg font-semibold text-terracotta uppercase tracking-wide">
								Objetivos
							</h4>
							<div className="space-y-3">
								{accion.objetivos.map((objetivo: string, index: number) => (
									<motion.div
										key={index}
										initial={{ opacity: 0, x: -20 }}
										whileInView={{ opacity: 1, x: 0 }}
										transition={{ duration: 0.6, delay: index * 0.1 }}
										viewport={{ once: true }}
										className="flex items-start gap-4 border-l-2 border-border pl-4"
									>
										<IconTarget className="w-4 h-4 text-terracotta shrink-0 mt-1" />
										<p className="font-body text-ink/80 leading-relaxed text-sm">
											{objetivo}
										</p>
									</motion.div>
								))}
							</div>
						</div>

						{/* Metodología */}
						<div className="space-y-4">
							<h4 className="font-display text-lg font-semibold text-terracotta uppercase tracking-wide">
								Metodología
							</h4>
							<div className="space-y-3">
								{accion.metodologia.map((metodo: string, index: number) => (
									<motion.div
										key={index}
										initial={{ opacity: 0, x: 20 }}
										whileInView={{ opacity: 1, x: 0 }}
										transition={{ duration: 0.6, delay: index * 0.1 }}
										viewport={{ once: true }}
										className="flex items-start gap-4 border-l-2 border-border pl-4"
									>
										<IconSettings className="w-4 h-4 text-ink/60 shrink-0 mt-1" />
										<p className="font-body text-ink/80 leading-relaxed text-sm">
											{metodo}
										</p>
									</motion.div>
								))}
							</div>
						</div>
					</div>
				</motion.div>

				{/* Detalles Técnicos */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, delay: 0.4 }}
					viewport={{ once: true }}
					className="mb-16"
				>
					<div className="mb-10">
						<h3 className="font-display text-2xl font-semibold text-ink">
							Detalles Técnicos
						</h3>
						<hr className="border-border mt-4" />
					</div>

					<div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
						{/* Público Destinatario */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.1 }}
							viewport={{ once: true }}
							className="space-y-3"
						>
							<div className="w-10 h-10 border border-border rounded-full flex items-center justify-center text-terracotta">
								<Icon
									iconId={accion.detallesTecnicos.publicoDestinatario.icono}
									className="w-5 h-5"
								/>
							</div>
							<h4 className="font-display text-sm font-semibold text-terracotta uppercase tracking-wide">
								Público Destinatario
							</h4>
							<p className="font-body text-ink/80 text-sm">
								{accion.detallesTecnicos.publicoDestinatario.texto}
							</p>
						</motion.div>

						{/* Formato */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.2 }}
							viewport={{ once: true }}
							className="space-y-3"
						>
							<div className="w-10 h-10 border border-border rounded-full flex items-center justify-center text-terracotta">
								<Icon
									iconId={accion.detallesTecnicos.formato.icono}
									className="w-5 h-5"
								/>
							</div>
							<h4 className="font-display text-sm font-semibold text-terracotta uppercase tracking-wide">
								Formato
							</h4>
							<div className="font-body text-ink/80 space-y-1 text-sm">
								{accion.detallesTecnicos.formato.texto.map(
									(texto: string, index: number) => (
										<p key={index}>{texto}</p>
									)
								)}
							</div>
						</motion.div>

						{/* Periodicidad */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.3 }}
							viewport={{ once: true }}
							className="space-y-3"
						>
							<div className="w-10 h-10 border border-border rounded-full flex items-center justify-center text-terracotta">
								<Icon
									iconId={accion.detallesTecnicos.periodicidad.icono}
									className="w-5 h-5"
								/>
							</div>
							<h4 className="font-display text-sm font-semibold text-terracotta uppercase tracking-wide">
								Periodicidad
							</h4>
							<div className="font-body text-ink/80 space-y-1 text-sm">
								{accion.detallesTecnicos.periodicidad.texto.map(
									(texto: string, index: number) => (
										<p key={index}>{texto}</p>
									)
								)}
							</div>
						</motion.div>

						{/* Espacios */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.4 }}
							viewport={{ once: true }}
							className="space-y-3"
						>
							<div className="w-10 h-10 border border-border rounded-full flex items-center justify-center text-terracotta">
								<Icon
									iconId={accion.detallesTecnicos.espacios.icono}
									className="w-5 h-5"
								/>
							</div>
							<h4 className="font-display text-sm font-semibold text-terracotta uppercase tracking-wide">
								Espacios
							</h4>
							<div className="font-body text-ink/80 space-y-1 text-sm">
								{accion.detallesTecnicos.espacios.texto.map(
									(texto: string, index: number) => (
										<p key={index}>{texto}</p>
									)
								)}
							</div>
						</motion.div>
					</div>
				</motion.div>

				{/* Resultados y Evaluación */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, delay: 0.6 }}
					viewport={{ once: true }}
				>
					<div className="mb-10">
						<h3 className="font-display text-2xl font-semibold text-ink">
							Resultados y Evaluación
						</h3>
						<hr className="border-border mt-4" />
					</div>

					<div className="grid lg:grid-cols-3 gap-12 items-start">
						{/* Contenido de texto */}
						<div className="lg:col-span-2 space-y-8">
							{/* Resultados */}
							<div className="space-y-3">
								<h4 className="font-display text-lg font-semibold text-terracotta uppercase tracking-wide">
									Resultados
								</h4>
								<div className="space-y-3">
									{accion.resultados.map(
										(resultado: string, index: number) => (
											<motion.div
												key={index}
												initial={{ opacity: 0, x: -20 }}
												whileInView={{ opacity: 1, x: 0 }}
												transition={{ duration: 0.6, delay: index * 0.1 }}
												viewport={{ once: true }}
												className="flex items-start gap-3"
											>
												<IconCheck className="w-4 h-4 text-terracotta shrink-0 mt-1" />
												<p className="font-body text-ink/80 leading-relaxed text-sm">
													{resultado}
												</p>
											</motion.div>
										)
									)}
								</div>
							</div>

							{/* Evaluación */}
							<div className="space-y-3">
								<h4 className="font-display text-lg font-semibold text-ink/60 uppercase tracking-wide">
									Evaluación
								</h4>
								<div className="space-y-3">
									{accion.evaluacion.map(
										(evaluacion: string, index: number) => (
											<motion.div
												key={index}
												initial={{ opacity: 0, x: -20 }}
												whileInView={{ opacity: 1, x: 0 }}
												transition={{ duration: 0.6, delay: index * 0.1 }}
												viewport={{ once: true }}
												className="flex items-start gap-3"
											>
												<IconChartBar className="w-4 h-4 text-ink/40 shrink-0 mt-1" />
												<p className="font-body text-ink/80 leading-relaxed text-sm">
													{evaluacion}
												</p>
											</motion.div>
										)
									)}
								</div>
							</div>
						</div>

						{/* Imagen de resultados */}
						<figure>
							<div className="relative w-full aspect-video rounded-xl border border-border overflow-hidden">
								<Image
									src={accion.result_img}
									alt={`Resultados de ${accion.titulo}`}
									fill
									sizes="(max-width: 1024px) 100vw, 33vw"
									className="object-cover"
								/>
							</div>
							<Caption>Resultados de {accion.subtitulo}</Caption>
						</figure>
					</div>
				</motion.div>
			</div>
		</section>
	);
}
